"use client";
/**
 * ShortlistScreen — what a customer has picked out on a digital-menu link.
 *
 * Deliberately terminal: it shows an estimate and stops there. There is no
 * checkout CTA, because there is nothing to check out to. v1's equivalent let
 * the cart fill and then failed at submit, which is the dead end this replaces.
 */
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect } from "react";

import { TopBar, Card, Button, Stepper } from "@/components/ui";
import { Icons } from "@/components/Icons";
import { Price } from "@/components/Price";
import { FoodImg } from "@/components/FoodImg";
import { useShortlist } from "@/store/shortlist";
import { track } from "@/lib/analytics";

export function ShortlistScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { shortlist, updateQty, removeLine, clearShortlist, shortlistTotal, orderNote, setOrderNote } =
    useShortlist();

  useEffect(() => {
    track("shortlist_viewed", { line_count: shortlist.length });
    // Once per visit, not once per edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ minHeight: "100%", paddingBottom: 32 }}>
      <TopBar title={t("v2.shortlist.title")} onBack={() => navigate("/")} />

      {shortlist.length === 0 ? (
        <div style={{ padding: "48px 24px", textAlign: "center" }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 999,
              background: "var(--surface-2)",
              display: "grid",
              placeItems: "center",
              margin: "0 auto 16px",
              color: "var(--ink-2)",
            }}
          >
            <Icons.bag size={24} />
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink-1)" }}>
            {t("v2.shortlist.empty_title")}
          </div>
          <div
            style={{
              fontSize: 13,
              color: "var(--ink-2)",
              marginTop: 6,
              lineHeight: 1.5,
            }}
          >
            {t("v2.shortlist.empty_body")}
          </div>
          <div style={{ marginTop: 20 }}>
            <Button onClick={() => navigate("/")}>
              {t("v2.shortlist.browse_menu")}
            </Button>
          </div>
        </div>
      ) : (
        <div style={{ padding: "12px 16px" }}>
          {shortlist.map((line) => (
            <Card key={line.id} style={{ marginBottom: 10 }} padding={12}>
              <div 
                style={{ display: "flex", gap: 12, alignItems: "flex-start", cursor: "pointer" }}
                onClick={() => navigate(`/item/${line.itemId}?editShortlistLineId=${line.id}`)}
              >
                <FoodImg
                  tone={line.tone}
                  src={line.imageUrl ?? undefined}
                  alt={line.name}
                  style={{ width: 56, height: 56, borderRadius: 10, flexShrink: 0 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      color: "var(--ink-1)",
                    }}
                  >
                    {line.name}
                  </div>
                  {line.modsLabel.length > 0 && (
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--ink-2)",
                        marginTop: 3,
                        lineHeight: 1.45,
                      }}
                    >
                      {line.modsLabel.join(" · ")}
                    </div>
                  )}
                  {line.notes && (
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--ink-3)",
                        marginTop: 3,
                        fontStyle: "italic",
                      }}
                    >
                      {line.notes}
                    </div>
                  )}
                    <div
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                        marginTop: 10,
                      }}
                    >
                      <Stepper
                        size="sm"
                        min={0}
                        value={line.qty}
                        onChange={(n) => updateQty(line.id, n)}
                      />
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>
                          <Price amount={line.price * line.qty} />
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeLine(line.id);
                          }}
                          aria-label={t("v2.shortlist.remove")}
                          style={{
                            background: "transparent",
                            border: 0,
                            color: "var(--ink-3)",
                            cursor: "pointer",
                            padding: 4,
                          }}
                        >
                          <Icons.trash size={16} />
                        </button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}

          <Card style={{ marginTop: 14 }} padding={14}>
            <div style={{ marginBottom: 14 }}>
              <div
                style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: "var(--ink-1)",
                  marginBottom: 8,
                }}
              >
                {t("v2.shortlist.order_note", "Order note")}
              </div>
              <textarea
                value={orderNote}
                onChange={(e) => setOrderNote(e.target.value)}
                placeholder={t("v2.shortlist.order_note_placeholder", "Any general requests for this order?")}
                style={{
                  width: "100%",
                  minHeight: 60,
                  resize: "vertical",
                  padding: 10,
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  fontSize: 13,
                  fontFamily: "inherit",
                  outline: "none",
                  background: "var(--surface-2)",
                  color: "var(--ink-1)",
                }}
              />
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink-1)" }}>
                {t("v2.shortlist.estimated_total")}
              </div>
              <div style={{ fontSize: 17, fontWeight: 800 }}>
                <Price amount={shortlistTotal} />
              </div>
            </div>
            <div
              style={{
                fontSize: 12,
                color: "var(--ink-2)",
                marginTop: 8,
                lineHeight: 1.5,
                borderInlineStart: "3px solid var(--border)",
                paddingInlineStart: 10,
              }}
            >
              {t("v2.shortlist.estimate_note")}
            </div>
          </Card>

          <div style={{ marginTop: 16, textAlign: "center" }}>
            <button
              onClick={clearShortlist}
              style={{
                background: "transparent",
                border: 0,
                color: "var(--ink-3)",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {t("v2.shortlist.clear_all")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
